import { CURRENTS, NEW_ROCKS, PIRATES, PIRATE_BASE, ROCK_MOTION, STREAM, WORLD_GEN, WORLD_RADIUS } from "../constants";
import { clamp, v2, vDist, vLen, type Vec2 } from "../math";
import { AREA, allAreas, cellBounds, cellCentre, cellsOfArea, singleAreaSphere, type AreaCoord, type Bounds, type SphereConfig } from "./areas";
import { biomeOf, environmentAt, heatAt, lightAt, livingHeat, mineralZone, type EnvironmentSources } from "./environment";
import { Rng, cellSeed, fbm2 } from "./rng";
import { asteroidHalf, branchReach, rockVines } from "./rocks";
import { bandWidth, makeName, makeStreams, nextStar, sphereKey, streamAt, streamsDistance, streamSqueeze, type StreamRoute } from "./stream";
import { bigEnoughRock, type AsteroidDef, type CurrentDef, type DerelictDef, type RingFlow, type SunDef, type VineDef, type WorldDef } from "./types";

const SUN_COLORS = [0xffd27a, 0xffb347, 0xfff1c4, 0xffa05a, 0xffe08a];

/** Clear space kept between padded asteroid grids and around suns, for flying. */
const FLIGHT_GAP = 80;
/** Derelicts per square block: as many for the space as the original one-area world had. */
const DERELICT_DENSITY = WORLD_GEN.derelicts / (Math.PI * (WORLD_RADIUS * 0.82) ** 2);
/** Of the big rocks far enough from the stream (see PIRATE_BASE.minDistance), this share are pirate bases. */
const PIRATE_BASE_CHANCE = 0.2;
/** Field rocks smaller than this are plain rock: too small to be hollow or hold a wreck. */
const SMALL_ROCK = 16;
/** A derelict ship's position is this far from its cell's edge (half its grid's diagonal, and some). */
const DERELICT_INSET = 60;
/** Everything a ring's rock sweeps, its grid's padding (a grove's too) and knocks and all, stays in the cell and the shell. */
const RING_PAD = WORLD_GEN.asteroidPad + 12 + FLIGHT_GAP / 2 + ROCK_MOTION.maxKnock;

/**
 * The parts of a world every area needs, whatever it covers: suns (their heat
 * reaches far), the streams' tracks and the rocks that ride them.
 * Small and quick.
 */
export interface GlobalLayer {
  suns: SunDef[];
  /** The rock streams (see stream.ts, makeStreams): one, or on a sphere with stars enough, several, each beside another for a stretch. By id. */
  streams: StreamRoute[];
  /** The rocks riding the streams (see streamRocks), every segment's of every stream. */
  streamRocks: AsteroidDef[];
  /** The cells that are dark reaches ("cx,cy"; see isReach). */
  reaches: Set<string>;
  /** Every star's rings, as the flow their rocks make (see systemLayout, flowAt). */
  rings: RingFlow[];
  /** Each star's system (by sun id): its size and rings (see systemLayout). */
  systems: Map<string, { size: SystemSize; rings: Ring[] }>;
}

const globals = new Map<string, GlobalLayer>();

/** The global layer of a sphere (made once per seed and size, then kept). */
export function generateGlobal(sphere: SphereConfig): GlobalLayer {
  const key = sphereKey(sphere);
  let g = globals.get(key);
  if (!g) {
    g = makeGlobal(sphere);
    globals.set(key, g);
  }
  return g;
}

/** Has this sphere's global layer been made (or adopted, see adoptGlobal) here yet? */
export function hasGlobal(sphere: Pick<SphereConfig, "seed" | "radius" | "areaSize">): boolean {
  return globals.has(sphereKey(sphere));
}

/** Use a global layer made elsewhere (see layout.ts) for this sphere, instead of making it here. */
export function adoptGlobal(sphere: Pick<SphereConfig, "seed" | "radius" | "areaSize">, layer: GlobalLayer): void {
  globals.set(sphereKey(sphere), layer);
}

/** Stream `id`'s track of the sphere a key stands for (see sphereKey): for rocks that only know the key (see RockDrift.stream). */
export function routeFor(key: string, id: number): StreamRoute {
  const g = globals.get(key);
  if (g) return g.streams[id]!;
  const [seed, radius, areaSize] = key.split(":").map(Number);
  return generateGlobal({ id: "world", seed: seed!, radius: radius!, areaSize: areaSize! }).streams[id]!;
}

/** How many times more of everything a sphere of this radius has than the original one. */
const scaleOf = (R: number) => (R / WORLD_RADIUS) ** 2;

/** Is cell (cx, cy) part of a dark reach (see WORLD_GEN.reaches): a cell with no star, in a region of them? */
export function isReach(sphere: SphereConfig, cx: number, cy: number): boolean {
  return generateGlobal(sphere).reaches.has(`${cx},${cy}`);
}

/**
 * Which of the cells that could hold a star are dark reaches instead: the
 * WORLD_GEN.reaches.share of them where a noise field over the sphere is
 * lowest, so reaches are regions, not scattered holes, and every sphere
 * has its share whatever its seed. Never all of them: a sphere needs a star.
 */
function reachCells(sphere: SphereConfig, cells: readonly [number, number][]): Set<string> {
  const R = WORLD_GEN.reaches;
  const scored = cells.map(([cx, cy]) => ({ key: `${cx},${cy}`, n: fbm2((sphere.seed ^ 0x5e4c) >>> 0, cx / R.scale + 0.37, cy / R.scale + 0.61) })).sort((a, b) => a.n - b.n);
  const dark = Math.min(cells.length - 1, Math.round(cells.length * R.share));
  return new Set(scored.slice(0, Math.max(0, dark)).map((c) => c.key));
}

/** Could cell (cx, cy) hold a star: well inside the shell? */
function starCell(sphere: SphereConfig, cx: number, cy: number): boolean {
  return vLen(cellCentre(sphere, cx, cy)) <= sphere.radius * 0.78;
}

function makeGlobal(sphere: SphereConfig): GlobalLayer {
  const R = sphere.radius;
  const rng = new Rng(sphere.seed);
  const names = new Set<string>();
  const nameRng = new Rng(sphere.seed ^ 0x3a9e5);
  // A star at the middle of every cell well inside the shell (each a solar system, see WORLD_GEN.systems) that isn't part of a dark reach: warm, or cold by the cell's own roll.
  const cells: [number, number][] = [];
  for (const area of allAreas(sphere)) for (const [cx, cy] of cellsOfArea(sphere, area)) if (starCell(sphere, cx, cy)) cells.push([cx, cy]);
  const reaches = reachCells(sphere, cells);
  const lit = cells.filter(([cx, cy]) => !reaches.has(`${cx},${cy}`));
  const suns: SunDef[] = [];
  const placed: { sun: SunDef; cx: number; cy: number }[] = [];
  for (const [cx, cy] of lit) {
    const position = cellCentre(sphere, cx, cy);
    const roll = new Rng(cellSeed(sphere.seed ^ 0x5011, cx, cy));
    // A sphere with one star has a warm one: there's nothing else to live by.
    const cold = roll.next() < WORLD_GEN.systems.cold && lit.length > 1;
    const [lo, hi] = WORLD_GEN.sunRadius;
    const name = makeName(nameRng, names);
    const sun: SunDef = cold
      ? { id: `sun-${suns.length}`, position, radius: roll.range(lo, hi * 0.8), color: 0x8fc8ff, output: roll.range(0.9, 1.2), kind: "cold", name }
      : { id: `sun-${suns.length}`, position, radius: roll.range(lo, hi), color: roll.pick(SUN_COLORS), output: roll.range(0.8, 1.3), name };
    suns.push(sun);
    placed.push({ sun, cx, cy });
  }
  // Each system's size and rings (see systemSizes), and the streams round their outermost rings: several where the stars make them (see
  // makeStreams), else one. Where they can't go by every star, the systems that aren't large are made a little smaller and they're tried again (and
  // the large ones too, at the last); for one stream, the route by the most stars is kept.
  let systems = new Map<string, { size: SystemSize; rings: Ring[] }>();
  let streams: StreamRoute[] = [];
  const covered = (rs: StreamRoute[]) => rs.reduce((n, r) => n + r.turns.size, 0);
  for (const mode of ["many", "single"] as const) {
    // Several streams only with the systems at their full size or nearly: smaller, and one stream it is.
    for (const [scale, largeScale] of (mode === "many" ? [[1, 1], [0.85, 1], [0.7, 1], [0.6, 0.85]] : [[1, 1], [0.85, 1], [0.7, 1], [0.6, 0.85]]) as readonly (readonly [number, number])[]) {
      const plans = systemSizes(sphere, placed, scale, largeScale);
      const tried = new Map<string, { size: SystemSize; rings: Ring[] }>();
      placed.forEach(({ sun, cx, cy }, i) => tried.set(sun.id, { size: plans[i]!.size, rings: systemRings(sphere, cx, cy, sun, plans[i]!.size, plans[i]!.reach) }));
      const routes = makeStreams(sphere, suns, (sun) => tried.get(sun.id)!.rings.at(-1)!.r1, new Set(names), mode);
      if (!routes.length) continue;
      if (!streams.length || covered(routes) > covered(streams)) [streams, systems] = [routes, tried];
      if (mode === "many" || covered(routes) >= suns.length) break;
    }
    if (streams.length) break;
  }
  // The names the routes took, kept (each name is used once).
  for (const r of streams) {
    names.add(r.name);
    for (const p of r.places) names.add(p.name);
  }
  // A system's outermost ring turns its stream's way where a stream goes round its star (so rim and belt nearly match), else whichever way its
  // roll says; each ring inside the other way from the one outside it; every rim at the same speed.
  const rings: RingFlow[] = [];
  for (const { sun, cx, cy } of placed) {
    const sys = systems.get(sun.id)!;
    const turn = streams.find((r) => r.turns.has(sun.id))?.turns.get(sun.id) ?? (new Rng(cellSeed(sphere.seed ^ 0x5017, cx, cy)).next() < 0.5 ? -1 : 1);
    sys.rings.forEach((g, k) => {
      const t = (sys.rings.length - 1 - k) % 2 === 0 ? turn : -turn;
      g.spin = Math.round(((t * WORLD_GEN.ring.speed) / g.r1) * 1e5) / 1e5;
      rings.push({ position: sun.position, r0: g.r0, r1: g.r1, spin: g.spin });
    });
  }

  const g: GlobalLayer = { suns, streams, streamRocks: [], reaches, rings, systems };
  g.streamRocks = streams.flatMap((route) => streamRocks(sphere, g, route, names));
  return g;
}

/** A star's ring: rocks whose outlines lie between `r0` and `r1` from it, going round at `spin` (rad/s). */
export interface Ring {
  r0: number;
  r1: number;
  spin: number;
  /** How many rocks it holds against the usual (see WORLD_GEN.ring.ringDensity). */
  density: number;
  /** It holds a giant (see WORLD_GEN.ring.giant). */
  giant?: boolean;
}

/** How big a system is (see WORLD_GEN.ring.sizes). */
export type SystemSize = "small" | "medium" | "large";

/** A solar system: its sun and its rings, innermost first. */
export interface SystemLayout {
  sun: SunDef;
  size: SystemSize;
  rings: Ring[];
}

/** The narrowest ring a giant is put in (as big as fits it, see belts). */
const GIANT_RING = 450;

/** The narrowest a ring near the shell is made so the stream fits round it (see systemRings), narrower than WORLD_GEN.ring.width allows elsewhere. */
const RING_MIN_FOR_STREAM = 200;

/**
 * A star's rings, out to `reach` from it (see WORLD_GEN.ring.sizes): from
 * just outside a flare's reach, one ring for a small system, else ring after
 * ring with lanes between, the last reaching right out. Their spins are set
 * once the stream's laid out (see makeGlobal). From the cell's own roll.
 */
function systemRings(sphere: SphereConfig, cx: number, cy: number, sun: SunDef, size: SystemSize, reach: number): Ring[] {
  const B = WORLD_GEN.ring;
  const rng = new Rng(cellSeed(sphere.seed ^ 0x5013, cx, cy));
  const inner = innerRing(sun);
  const out: Ring[] = [];
  const density = () => Math.round(rng.range(B.ringDensity[0], B.ringDensity[1]) * 100) / 100;
  if (size === "small" || reach - inner < B.ringWidth[0] * 2 + B.lane) {
    const width = reach - inner;
    out.push({ r0: inner, r1: Math.round(reach), spin: 0, density: density(), ...(width >= GIANT_RING && rng.next() < B.giant.chance ? { giant: true } : {}) });
    return out;
  }
  // Now and then one ring past the first is a giant's: wide enough for one.
  const giantAt = rng.next() < B.giant.chance ? rng.int(1, 3) : -1;
  let r = inner;
  for (;;) {
    const k = out.length;
    const w = k === giantAt ? B.giant.width : rng.range(B.ringWidth[0], B.ringWidth[1]);
    // Not room for another after this one: this one reaches right out.
    if (r + w + B.lane + B.ringWidth[0] > reach) {
      out.push({ r0: Math.round(r), r1: Math.round(reach), spin: 0, density: density(), ...(k === giantAt && reach - r >= GIANT_RING ? { giant: true } : {}) });
      break;
    }
    out.push({ r0: Math.round(r), r1: Math.round(r + w), spin: 0, density: density(), ...(k === giantAt ? { giant: true } : {}) });
    r += w + B.lane;
  }
  return out;
}

/** Where a star's innermost ring starts: just outside a flare's reach. */
function innerRing(sun: SunDef): number {
  return Math.round(sun.radius + WORLD_GEN.life.scorched * 0.5 + 60);
}

/**
 * How far each star's system reaches (see WORLD_GEN.ring.sizes): its size
 * rolled (a large one never beside another), then
 * as far as that size says within its cell and the shell, leaving room for
 * the stream round it (see makeStreamRoute), and between neighbours room
 * for the stream to pass between them: beside a large one the other gives
 * way, else the bigger. `scale` (and `largeScale`, for the large ones)
 * shrinks them toward their smallest, for a route that won't fit (see
 * makeGlobal).
 */
function systemSizes(sphere: SphereConfig, stars: { sun: SunDef; cx: number; cy: number }[], scale = 1, largeScale = 1): { size: SystemSize; reach: number }[] {
  const S = WORLD_GEN.ring.sizes;
  const pos = stars.map((s) => s.sun.position);
  const nearest = stars.length > 1 ? Math.min(...pos.map((p, i) => Math.min(...pos.filter((_, j) => j !== i).map((q) => vDist(p, q))))) : Infinity;
  const near = (i: number, j: number) => i !== j && vDist(pos[i]!, pos[j]!) <= nearest * 1.6;
  // How far each could reach at most: its cell, the shell, and room for the stream round it inside the shell.
  const caps = stars.map(({ sun, cx, cy }) => {
    const b = cellBounds(sphere, cx, cy);
    const inner = innerRing(sun);
    const streamRoom = sphere.radius * 0.88 - STREAM.shellGap - vLen(sun.position) - STREAM.ringGap - STREAM.band / 2 - 2;
    // A lone star leaves the wild room between the stream round it and the shell (pirates and worse come only so far from the stream).
    const wild = stars.length === 1 ? streamRoom - PIRATES.safeDistance - 100 : Infinity;
    return Math.min((b.x1 - b.x0) / 2 - RING_PAD, sphere.radius * 0.88 - vLen(sun.position) - RING_PAD, streamRoom >= inner + RING_MIN_FOR_STREAM ? streamRoom : Infinity, Math.max(inner + RING_MIN_FOR_STREAM, wild));
  });
  // Each one's size by its roll, no bigger than it has room for, and never a large one beside another.
  const sizes: SystemSize[] = [];
  stars.forEach(({ cx, cy }, i) => {
    const roll = new Rng(cellSeed(sphere.seed ^ 0x5019, cx, cy)).next();
    let size: SystemSize = roll < S.large.share ? "large" : roll < S.large.share + S.medium.share ? "medium" : "small";
    if (size === "large" && (caps[i]! < S.large.reach[0] || sizes.some((s, j) => s === "large" && near(i, j)))) size = "medium";
    if (size === "medium" && caps[i]! < S.medium.reach[0]) size = "small";
    sizes.push(size);
  });
  const reach = stars.map(({ sun, cx, cy }, i) => {
    const inner = innerRing(sun);
    const cap = caps[i]!;
    const [lo, hi] = S[sizes[i]!].reach;
    const floor = inner + RING_MIN_FOR_STREAM;
    const want = floor + (new Rng(cellSeed(sphere.seed ^ 0x501b, cx, cy)).range(lo, hi) - floor) * (sizes[i] === "large" ? largeScale : scale);
    return Math.max(floor, Math.min(want, cap));
  });
  // Neighbours leave the stream room to pass between them (both its corridors, and a gap): the bigger gives way.
  const passing = 2 * (STREAM.ringGap + STREAM.band / 2) + 2 * STREAM.corridor + 100;
  for (let round = 0; round < 3; round++) {
    for (let i = 0; i < stars.length; i++) {
      for (let j = i + 1; j < stars.length; j++) {
        if (!near(i, j)) continue;
        const room = vDist(pos[i]!, pos[j]!) - passing;
        const over = reach[i]! + reach[j]! - room;
        if (over <= 0) continue;
        // A large system keeps its size: the other gives way (down to its smallest), then it does if it must. Otherwise the bigger gives way.
        const order = sizes[i] === "large" ? [j, i] : sizes[j] === "large" ? [i, j] : reach[i]! >= reach[j]! ? [i, j] : [j, i];
        let left = over;
        for (const k of order) {
          const floor = innerRing(stars[k]!.sun) + RING_MIN_FOR_STREAM;
          const give = Math.min(left, reach[k]! - floor);
          reach[k] = reach[k]! - give;
          left -= give;
        }
      }
    }
  }
  return sizes.map((size, i) => ({ size, reach: Math.round(reach[i]!) }));
}

/**
 * How cell (cx, cy)'s star's system is laid out, if it has a star: its rings
 * (see systemRings), the outermost turning the stream's way where the stream
 * goes round the star (so rim and belt nearly match, and the star is an
 * on-ramp and an off-ramp), else whichever way its roll says, and each ring
 * inside it the other way from the one outside it. Known without making any
 * rocks.
 */
export function systemLayout(sphere: SphereConfig, cx: number, cy: number): SystemLayout | null {
  const c = cellCentre(sphere, cx, cy);
  const global = generateGlobal(sphere);
  const sun = global.suns.find((s) => s.position.x === c.x && s.position.y === c.y);
  if (!sun) return null;
  const sys = global.systems.get(sun.id)!;
  return { sun, size: sys.size, rings: sys.rings.map((g) => ({ ...g })) };
}

/** Distance from the sphere's middle to the nearest point of a box. */
function distanceToBox(b: Bounds): number {
  return Math.hypot(Math.max(b.x0, 0, -b.x1), Math.max(b.y0, 0, -b.y1));
}

/** What the environment of a sphere's rocks is worked out from (see environmentAt). */
export function environmentSources(sphere: SphereConfig): EnvironmentSources {
  const g = generateGlobal(sphere);
  return { suns: g.suns };
}

/** What a cell holds. */
export interface CellContents {
  asteroids: AsteroidDef[];
  derelicts: DerelictDef[];
  vines: VineDef[];
  /** Eddies turning fields round (see CurrentDef.spin). */
  currents: CurrentDef[];
}

/**
 * Make a small rock's def at `center`: its kind (from its seed), whether
 * pirates hold it, its environment and biome. Near a warm sun (see
 * WORLD_GEN.living) it may grow into a living rock instead, bigger the closer
 * it is. Returns null if it doesn't fit (see `fits`).
 */
function makeRock(
  sphere: SphereConfig,
  id: string,
  center: Vec2,
  radius: number,
  seed: number,
  rng: Rng,
  fits: (center: Vec2, half: number) => boolean,
  opts: { living?: boolean; pirates?: boolean; nuggets?: boolean } = {},
): AsteroidDef | null {
  const src = environmentSources(sphere);
  const global = generateGlobal(sphere);
  const def: AsteroidDef = { id, ox: 0, oy: 0, w: 0, h: 0, radius, seed, kind: "rock" };
  const heat = heatAt(src.suns, center, radius);
  const zone = mineralZone(heat);
  def.kind = rockKind(def, zone);
  if (radius < SMALL_ROCK && (def.kind === "hollow" || def.kind === "dead")) def.kind = "rock";
  // Now and then a small one is a nugget: almost all coal or iron ore.
  const N = WORLD_GEN.minerals.nuggets;
  if (opts.nuggets && radius <= N.radius) {
    const roll = rng.next();
    if (roll < N[zone].iron) def.kind = "iron";
    else if (roll < N[zone].iron + N[zone].coal) def.kind = "coal";
  }
  // Further from the streams, richer: by how many cells away from the nearest it is (see AREA.cellTarget).
  const W = WORLD_GEN.minerals.wealth;
  const away = Math.round(streamsDistance(global.streams, center) / AREA.cellTarget);
  const wealth = 1 + Math.min(W.max, W.per * away);
  if (wealth > 1) def.wealth = wealth;
  const living = WORLD_GEN.living.enabled && (opts.living ?? (livingHeat(heat) && LIVING.includes(def.kind) && rng.next() < WORLD_GEN.living.chance));
  if (living) {
    const L = WORLD_GEN.living;
    const near = clamp((heat - L.heat) / (WORLD_GEN.biomes.scorched - L.heat), 0, 1);
    def.kind = "living";
    def.radius = Math.round((L.radius[0] + (L.radius[1] - L.radius[0]) * near) * rng.range(0.85, 1.1) * (opts.living ? 0.85 : 1));
    def.reach = branchReach(def.radius);
    const sun = nearestWarmSun(global.suns, center);
    if (sun) def.sunward = Math.round(Math.atan2(sun.position.y - center.y, sun.position.x - center.x) * 1000) / 1000;
  }
  if (opts.pirates !== false && !living && def.radius >= WORLD_GEN.ring.big[0] && streamsDistance(global.streams, center) > PIRATE_BASE.minDistance && rng.next() < PIRATE_BASE_CHANCE) def.pirateBase = true;
  const env = environmentAt(src, center, def.radius, def.kind, def.seed);
  def.biome = biomeOf(env, def.kind, def.pirateBase);
  if (env.light) def.light = env.light;
  if (env.heat) def.heat = env.heat;
  if (env.wet) def.wet = env.wet;
  if (bigEnoughRock(def)) def.big = true;
  const half = asteroidHalf(def.radius, def.kind, def.reach ?? 0, def.biome);
  if (!fits(center, half)) return null;
  Object.assign(def, { ox: center.x - half, oy: center.y - half, w: half * 2, h: half * 2 });
  return def;
}

/** The warm sun nearest `p`, if any. */
function nearestWarmSun(suns: readonly SunDef[], p: Vec2): SunDef | undefined {
  let best: SunDef | undefined;
  for (const s of suns) if (s.kind !== "cold" && (!best || vDist(s.position, p) - s.radius < vDist(best.position, p) - best.radius)) best = s;
  return best;
}

/**
 * What one world-generation cell holds. Each is a solar system: with a
 * star at its middle (see WORLD_GEN.systems), a ring of rocks going round it
 * (see belts); without (a dark reach, or out by the shell), a few things
 * (see WORLD_GEN.cells and WORLD_GEN.reaches), each a field of small rocks
 * (maybe turning in an eddy), a big rock on its own (maybe a pirate base),
 * or open space. The banks of the stream, where it passes through. Maybe a
 * derelict too. Made from the sphere's seed, the cell's coordinates and the
 * global layer, nothing else, so any area can be made on its own. Nothing
 * crosses the cell's edges (a rock however it drifts), so nothing ever
 * straddles two areas; and nothing goes in the stream's corridor (see
 * STREAM.corridor), where the stream's own rocks ride through.
 */
export function generateCell(sphere: SphereConfig, cx: number, cy: number): CellContents {
  const R = sphere.radius;
  const b = cellBounds(sphere, cx, cy);
  const out: CellContents = { asteroids: [], derelicts: [], vines: [], currents: [] };
  if (distanceToBox(b) > R * 0.88) return out;
  const global = generateGlobal(sphere);
  const rng = new Rng(cellSeed(sphere.seed, cx, cy));
  const all = () => out.asteroids;
  // The cell's size against the original's (1600 across): it holds that many times as much.
  const scale = ((b.x1 - b.x0) / 1600) ** 2;
  const offStream = (center: Vec2, half: number) => streamsDistance(global.streams, center) > STREAM.corridor + half * 1.42;
  const clear = (center: Vec2, half: number, gap = FLIGHT_GAP) =>
    inCell(b, center, half) &&
    vLen(center) + half * 1.42 < R * 0.88 &&
    offStream(center, half) &&
    !global.suns.some((s) => vDist(s.position, center) < s.radius * 2 + half * 1.42 + FLIGHT_GAP) &&
    !all().some((a) => vDist(sweptMiddle(a), center) < (a.w / 2 + sweptReach(a) + half) * 1.42 + gap);

  // The stream's banks first: they're where the track says, and the rest keeps clear of them.
  banks(sphere, cx, cy, b, global, out);

  const system = systemLayout(sphere, cx, cy);
  const reach = !system && starCell(sphere, cx, cy);
  if (system) belts(sphere, cx, cy, system, rng, out);
  else {
    const things = reach ? WORLD_GEN.reaches.things : WORLD_GEN.cells.things;
    for (let k = 0; k < Math.round(things * scale); k++) {
      const roll = rng.next();
      if (roll < WORLD_GEN.cells.field) field(sphere, cx, cy, b, rng, out, clear);
      else if (roll < WORLD_GEN.cells.field + WORLD_GEN.cells.lone) {
        for (let tries = 0; tries < 16; tries++) {
          const radius = Math.round(rng.range(WORLD_GEN.lone.radius[0], WORLD_GEN.lone.radius[1]));
          const center = v2(Math.round(rng.range(b.x0, b.x1)), Math.round(rng.range(b.y0, b.y1)));
          const def = makeRock(sphere, `r${cx}_${cy}_${out.asteroids.length}`, center, radius, rng.int(1, 0x7fffffff), rng, clear);
          if (!def) continue;
          out.asteroids.push(def);
          break;
        }
      }
    }
  }

  const area = (b.x1 - b.x0) * (b.y1 - b.y0);
  const derelicts = Math.floor(DERELICT_DENSITY * area * (reach ? WORLD_GEN.reaches.derelicts : 1) + rng.next());
  for (let k = 0; k < derelicts; k++) {
    for (let tries = 0; tries < 20; tries++) {
      const position = v2(rng.range(b.x0 + DERELICT_INSET, b.x1 - DERELICT_INSET), rng.range(b.y0 + DERELICT_INSET, b.y1 - DERELICT_INSET));
      if (vLen(position) > R * 0.82) continue;
      if (global.suns.some((s) => vDist(s.position, position) < s.radius * 2 + 120)) continue;
      if (!offStream(position, 60)) continue;
      // Clear of every rock's path (a lane between rings will do) and of the other derelicts.
      if (all().some((a) => sweeps(a, position, 120))) continue;
      if (out.derelicts.some((d) => vDist(d.position, position) < 200)) continue;
      // Not in an eddy, or it would drift off for ever (and its area never sleep).
      if (out.currents.some((c) => vDist(c.position, position) < c.radius + 60)) continue;
      out.derelicts.push({ id: `derelict-${cx}_${cy}${k ? `_${k}` : ""}`, position, kind: rng.next() < 0.35 ? "hulk" : "derelict", seed: rng.int(1, 0x7fffffff) });
      break;
    }
  }
  return out;
}

/**
 * The static rocks lining the stream where it passes through a cell (see
 * BankSpec, stream.ts): kerbs, the walls of a narrows, a gorge's rock, lone
 * rocks to stand on. Those that don't fit in the cell (straddling its edge)
 * are left out. A gorge's rock doesn't pull, whatever its size: the stream
 * runs through it, and a rider mustn't fall off their rock into its walls.
 */
function banks(sphere: SphereConfig, cx: number, cy: number, b: Bounds, global: GlobalLayer, out: CellContents): void {
  const R = sphere.radius;
  for (const spec of global.streams.flatMap((r) => r.banks)) {
    const { at } = spec;
    if (at.x < b.x0 || at.x >= b.x1 || at.y < b.y0 || at.y >= b.y1) continue;
    const rng = new Rng(spec.seed);
    const fits = (center: Vec2, half: number) =>
      inCell(b, center, half) && vLen(center) + half * 1.42 < R * 0.88 && !out.asteroids.some((a) => vDist(sweptMiddle(a), center) < (a.w / 2 + half) * 1.42 + 20);
    const def = makeRock(sphere, `k${cx}_${cy}_${out.asteroids.length}`, at, spec.radius, spec.seed, rng, fits, { living: false, pirates: false, nuggets: spec.kind !== "gorge" });
    if (!def) continue;
    if (spec.kind === "gorge") {
      delete def.big;
      def.kind = def.kind === "ice" ? "ice" : "rock";
      def.channel = spec.channel;
    }
    out.asteroids.push(def);
  }
}

/**
 * A field (see WORLD_GEN.field): small rocks scattered through a disc clear of
 * everything else, all drifting (see AsteroidDef.drift). An eddy's disc turns
 * round its middle like a wheel, so its rocks never meet unless knocked; the
 * whole disc they sweep, padding and all, is inside the cell.
 */
function field(sphere: SphereConfig, cx: number, cy: number, b: Bounds, rng: Rng, out: CellContents, clear: (c: Vec2, half: number) => boolean): void {
  const F = WORLD_GEN.field;
  const maxHalf = asteroidHalf(F.rockRadius[1]);
  let middle: Vec2 | null = null;
  let r = 0;
  for (let tries = 0; tries < 16 && !middle; tries++) {
    r = Math.round(rng.range(F.radius[0], F.radius[1]));
    const p = v2(Math.round(rng.range(b.x0, b.x1)), Math.round(rng.range(b.y0, b.y1)));
    if (clear(p, r + maxHalf)) middle = p;
  }
  if (!middle) return;
  const spin = (rng.range(F.rimSpeed[0], F.rimSpeed[1]) / r) * (rng.next() < 0.5 ? -1 : 1);
  const eddy = rng.next() < F.eddyChance ? { x: middle.x, y: middle.y, r, spin: Math.round(spin * 1e5) / 1e5 } : undefined;
  // Its own id among the cell's fields (several share a cell), so no two rocks share one.
  const id = `f${cx}_${cy}_${out.asteroids.length}`;
  const members: AsteroidDef[] = [];
  const want = rng.int(F.rocks[0], F.rocks[1]);
  for (let tries = 0; members.length < want && tries < want * 12; tries++) {
    const radius = rng.int(F.rockRadius[0], F.rockRadius[1]);
    const at = pointInDisc(rng, r);
    const center = v2(Math.round(middle.x + at.x), Math.round(middle.y + at.y));
    // Clear of the others by their widest outlines and the gap.
    if (members.some((m) => vDist(centerOf(m), center) < (m.radius + radius) * 1.25 + F.gap)) continue;
    const def = makeRock(sphere, `${id}_${members.length}`, center, radius, rng.int(1, 0x7fffffff), rng, () => true, { living: false, pirates: false });
    if (!def) continue;
    def.drift = eddy ? { x: def.ox, y: def.oy, eddy } : { x: def.ox, y: def.oy };
    members.push(def);
  }
  out.asteroids.push(...members);
  if (eddy && members.length) out.currents.push({ position: middle, radius: r + maxHalf / 2, flow: v2(), spin: eddy.spin });
}

/**
 * A star's rings (see systemLayout): each scattered with rocks, small, medium
 * and now and then big (see isBigRock; far from the stream, they
 * may be pirate bases), every one wholly inside its ring; the system always
 * has a large rock (in its second ring, or its only one), a giant now and
 * then, and maybe a water ball. The ring turns like a
 * wheel (see AsteroidDef.drift), so its rocks never meet unless knocked;
 * going round keeps a rock as far from its star, so the light and heat it
 * was made for stay true.
 */
function belts(sphere: SphereConfig, cx: number, cy: number, system: SystemLayout, rng: Rng, out: CellContents): void {
  const B = WORLD_GEN.ring;
  const { sun } = system;
  const id = `b${cx}_${cy}`;
  let waters = rng.int(B.water[0], B.water[1]);
  const members: AsteroidDef[] = [];
  system.rings.forEach((g, k) => {
    const eddy = { x: sun.position.x, y: sun.position.y, r: g.r1, inner: g.r0, spin: g.spin };
    const mine: AsteroidDef[] = [];
    const area = Math.PI * (g.r1 * g.r1 - g.r0 * g.r0);
    const want = Math.round(B.density * g.density * area);
    const fits = (radius: number) => radius * 2.5 <= g.r1 - g.r0;
    const put = (def: AsteroidDef) => {
      def.drift = { x: def.ox, y: def.oy, eddy };
      mine.push(def);
    };
    /** A rock of `radius` somewhere in the ring, clear of the others: its centre, or null if there's no room. */
    const spot = (radius: number): Vec2 | null => {
      for (let tries = 0; tries < 12; tries++) {
        const a = rng.range(0, Math.PI * 2);
        const r = rng.range(g.r0 + radius * 1.25, g.r1 - radius * 1.25);
        const c = v2(Math.round(sun.position.x + Math.cos(a) * r), Math.round(sun.position.y + Math.sin(a) * r));
        if (!mine.some((m) => vDist(centerOf(m), c) < (m.radius + radius) * 1.25 + B.gap)) return c;
      }
      return null;
    };
    // The large rock first (in the second ring, or the only one), then the giant if it's a giant's ring: as big as fits.
    if (k === Math.min(1, system.rings.length - 1)) {
      const radius = Math.min(rng.int(B.large.radius[0], B.large.radius[1]), Math.floor((g.r1 - g.r0) / 2.5));
      const c = spot(radius);
      const def = c && makeRock(sphere, `${id}r${k}_${mine.length}`, c, radius, rng.int(1, 0x7fffffff), rng, () => true, { living: false });
      if (def) put(def);
    }
    if (g.giant) {
      const radius = Math.min(B.giant.radius, Math.floor((g.r1 - g.r0) / 2.5));
      const c = spot(radius);
      const def = c && makeRock(sphere, `${id}r${k}g`, c, radius, rng.int(1, 0x7fffffff), rng, () => true, { living: false, pirates: false });
      if (def) put(def);
    }
    // A water ball now and then.
    if (waters > 0 && rng.next() < 0.6) {
      const radius = rng.int(B.waterRadius[0], B.waterRadius[1]);
      const c = fits(radius) ? spot(radius) : null;
      if (c) {
        const half = asteroidHalf(radius);
        put({ id: `${id}r${k}w`, ox: c.x - half, oy: c.y - half, w: half * 2, h: half * 2, radius, seed: rng.int(1, 0x7fffffff), kind: "water", biome: "water" });
        waters--;
      }
    }
    for (let n = 0, tries = 0; n < want && tries < want * 3; tries++) {
      const roll = rng.next();
      const size = roll < B.shares.big && fits(B.big[0]) ? B.big : roll < B.shares.big + B.shares.medium && fits(B.medium[0]) ? B.medium : B.small;
      const radius = rng.int(size[0], Math.min(size[1], Math.floor((g.r1 - g.r0) / 2.5)));
      const c = spot(radius);
      if (!c) continue;
      const def = makeRock(sphere, `${id}r${k}_${mine.length}`, c, radius, rng.int(1, 0x7fffffff), rng, () => true, { living: false, pirates: size === B.big, nuggets: true });
      if (!def) continue;
      put(def);
      // A big rock counts for as many small ones as it's in the way of.
      n += Math.max(1, Math.round((radius / B.small[1]) ** 2));
    }
    members.push(...mine);
  });
  out.asteroids.push(...members);
  // Some of its medium rocks are vined: lobes with open space between, tied together by vines. Picked with rolls of their own.
  const roll = new Rng(cellSeed(sphere.seed ^ 0x7155, cx, cy));
  for (const def of members) {
    if (def.big || def.radius < B.medium[0] || (def.kind !== "rock" && def.kind !== "metal" && def.kind !== "ice") || roll.next() >= B.vined.share) continue;
    def.vined = true;
    out.vines.push(...rockVines(def));
  }
}

// ----------------------------------------------------------------- stream

/** Half the side of a stream rock's grid: its rock, and padding to build out into (less for the small ones, so the next rock along is in reach). */
function streamHalf(radius: number, big: boolean): number {
  return Math.ceil(radius * 1.25) + (big ? STREAM.bigPad : STREAM.pad);
}

/**
 * The rocks riding one stream (see STREAM): placed over the whole band in
 * one go from the seed, so every area and client makes the same, and cut
 * into segments by where they are on it (see segmentOf). The named big
 * rocks (where new players start) go first, mid-band and spaced evenly round
 * the loop; then the large asteroids (STREAM.large), near the middle; then the rest, biggest first, each dropped at random on the band
 * (big ones toward its middle, small ones out to its edges) wherever its
 * grid can never touch another's anywhere on the loop, till their grids
 * cover STREAM.fill of the band. Each grows what the light where it's made
 * gives it, as any rock does, though it carries on round the loop from there.
 */
function streamRocks(sphere: SphereConfig, global: GlobalLayer, route: StreamRoute, names: Set<string>): AsteroidDef[] {
  const S = STREAM;
  const key = sphereKey(sphere);
  const id = route.id;
  const rng = new Rng((sphere.seed ^ 0x5a0c7) + id * 0x3b5d1);
  const nameRng = new Rng((sphere.seed ^ 0x3a9e7) + id * 0x3b5d1);
  const length = route.length;
  // In the band's frame (u along the belt, o across it) each grid is a circle round its middle: a square that doesn't turn with the track reaches root two of its half any way round.
  type Spot = { u: number; o: number; r: number };
  const spots: Spot[] = [];
  const BIN = 200;
  const bins: Spot[][] = Array.from({ length: Math.ceil(length / BIN) }, () => []);
  const pinch = S.narrows.width / S.band;
  // Along the belt, how far apart two rocks must be never to touch: on the inside of the tightest bend the belt's pushed together (across the band it isn't),
  // and at a narrows the band's drawn in (along it, it isn't). A little to spare for the bend's chord.
  const need = (a: Spot, b: Spot): number => {
    const rr = a.r + b.r;
    const across = Math.abs(a.o - b.o);
    const bend = across >= rr ? 0 : (Math.sqrt(rr * rr - across * across) / Math.min(streamSqueeze(route, a.o), streamSqueeze(route, b.o))) * 1.04;
    const narrow = across * pinch >= rr ? 0 : Math.sqrt(rr * rr - (across * pinch) ** 2);
    return Math.max(bend, narrow);
  };
  const farthest = (2 * streamHalf(Math.max(S.namedRadius[1], S.large.radius[1]), true) * 1.42 * 1.04) / streamSqueeze(route, S.band / 2);
  const reach = Math.ceil(farthest / BIN);
  const mod = (x: number) => ((x % length) + length) % length;
  const fits = (a: Spot): boolean => {
    const b0 = Math.floor(a.u / BIN);
    for (let k = -reach; k <= reach; k++) {
      for (const b of bins[(((b0 + k) % bins.length) + bins.length) % bins.length]!) {
        const du = Math.abs(mod(a.u - b.u + length / 2) - length / 2);
        if (du < need(a, b)) return false;
      }
    }
    return true;
  };
  const take = (a: Spot) => {
    spots.push(a);
    bins[Math.floor(a.u / BIN) % bins.length]!.push(a);
  };
  const out: AsteroidDef[] = [];
  const make = (u: number, o: number, radius: number, half: number, named?: string): AsteroidDef => {
    const seed = rng.int(1, 0x7fffffff);
    const def: AsteroidDef = { id: `st${id}_${out.length}`, ox: 0, oy: 0, w: half * 2, h: half * 2, radius, seed, kind: "rock", heat: 0.4 };
    if (named) {
      def.named = named;
      def.big = true;
      def.biome = "twilight";
      def.light = 0.5;
    } else {
      def.kind = rockKind(def, "mild");
      if (radius < SMALL_ROCK && (def.kind === "hollow" || def.kind === "dead")) def.kind = "rock";
      const N = WORLD_GEN.minerals.nuggets;
      if (radius <= N.radius) {
        const roll = rng.next();
        if (roll < N.mild.iron * 2) def.kind = "iron";
        else if (roll < N.mild.iron * 2 + N.mild.coal * 2) def.kind = "coal";
      }
      if (bigEnoughRock(def)) def.big = true;
    }
    const { p, dir } = streamAt(route, u, 0);
    const w = bandWidth(route, u) / S.band;
    if (!named) {
      // Its life from the light where it's made (as any rock's), though it goes on round the loop from there; mild heat all the way.
      const env = environmentAt({ suns: global.suns },v2(p.x - dir.y * o * w, p.y + dir.x * o * w), radius, def.kind, seed);
      def.biome = biomeOf({ ...env, heat: 0.4 }, def.kind);
      if (env.light) def.light = env.light;
      if (env.wet) def.wet = env.wet;
    }
    def.ox = Math.round(p.x - dir.y * o * w - half);
    def.oy = Math.round(p.y + dir.x * o * w - half);
    def.drift = { x: def.ox, y: def.oy, stream: { key, id, u, o } };
    return def;
  };
  // The named rocks, evenly round the loop, mid-band.
  const named = Math.max(1, Math.min(S.named, Math.floor(length / 1500)));
  for (let i = 0; i < named; i++) {
    const radius = rng.int(S.namedRadius[0], S.namedRadius[1]);
    const half = streamHalf(radius, true);
    const u = Math.round(((i + 0.5) * length) / named * 100) / 100;
    take({ u, o: 0, r: half * 1.42 });
    out.push(make(u, 0, radius, half, makeName(nameRng, names)));
  }
  // The large asteroids: about one every STREAM.large.every blocks, near the middle of the band, between the named rocks.
  const large = Math.floor(length / S.large.every);
  for (let i = 0; i < large; i++) {
    const radius = rng.int(S.large.radius[0], S.large.radius[1]);
    const half = streamHalf(radius, true);
    const room = Math.max(0, S.band / 2 - radius * 1.25 - 2);
    for (let tries = 0; tries < 12; tries++) {
      const spot = { u: Math.round(((i + rng.range(0.15, 0.85)) * length) / large * 100) / 100, o: Math.round(rng.range(-room, room) * 10) / 10, r: half * 1.42 };
      if (!fits(spot)) continue;
      take(spot);
      out.push(make(spot.u, spot.o, radius, half));
      break;
    }
  }
  // How many of the rest: enough for their grids to cover `fill` of the band, by the shares of each size.
  const sizes = [S.small, S.medium, S.big] as const;
  const shares = [1 - S.shares.medium - S.shares.big, S.shares.medium, S.shares.big];
  const area = (size: readonly [number, number]) => {
    const r = (size[0] + size[1]) / 2;
    return (2 * streamHalf(r, r >= WORLD_GEN.bigRadius)) ** 2;
  };
  const meanArea = sizes.reduce((n, size, i) => n + shares[i]! * area(size), 0);
  const count = Math.round((S.fill * length * S.band) / meanArea);
  const radii: number[] = [];
  for (let i = 0; i < count; i++) {
    const roll = rng.next();
    const size = roll < S.shares.big ? S.big : roll < S.shares.big + S.shares.medium ? S.medium : S.small;
    radii.push(rng.int(size[0], size[1]));
  }
  radii.sort((a, b) => b - a);
  for (const radius of radii) {
    const big = radius >= WORLD_GEN.bigRadius;
    const half = streamHalf(radius, big);
    // Every rock's outline stays inside the band.
    const room = Math.max(0, S.band / 2 - radius * 1.25 - 2);
    for (let tries = 0; tries < 40; tries++) {
      const side = rng.next() < 0.5 ? -1 : 1;
      // Big ones toward the middle, medium about it, small out toward the edges.
      const o = Math.round(side * room * (big ? rng.range(0, 0.4) : radius >= S.medium[0] ? rng.range(0, 0.75) : Math.sqrt(rng.next())) * 10) / 10;
      const u = Math.round(rng.range(0, length) * 100) / 100;
      const spot = { u, o, r: half * 1.42 };
      if (!fits(spot)) continue;
      take(spot);
      out.push(make(u, o, radius, half));
      break;
    }
  }
  // In order along the belt, as the segments cut them.
  out.sort((a, b) => a.drift!.stream!.u - b.drift!.stream!.u);
  out.forEach((d, i) => (d.id = `st${id}_${i}`));
  return out;
}

/** The rocks of segment `k` of stream `stream` (see segmentOf): any area or segment can make a passing segment's rocks without asking. */
export function generateSegment(sphere: SphereConfig, stream: number, k: number): AsteroidDef[] {
  const g = generateGlobal(sphere);
  const L = g.streams[stream]!.segmentLength;
  return g.streamRocks.filter((d) => {
    const st = d.drift!.stream!;
    return st.id === stream && st.u >= k * L && st.u < (k + 1) * L;
  });
}

/** Every stream's named big rocks (see STREAM.named), where new players start, stream by stream, in order along each belt. */
export function namedRocks(sphere: SphereConfig): AsteroidDef[] {
  return generateGlobal(sphere).streamRocks.filter((d) => d.named);
}

/**
 * Where new players start (and the knocked out with no bed) at wall-clock
 * time `nowMs`, from the seed and the clock alone: of every stream's named
 * big rocks, the one in sunlight now (STREAM.lit) with a star soonest ahead
 * of it (within STREAM.starAhead seconds); failing that, the best lit. So
 * everyone starts in the same handful of places, starters meet, nobody
 * starts in the dark, and the Worker can say which area to join without
 * waking anything. Null in a world with no stream rocks. The rock's grid is
 * where it is at `nowMs` (settling aside, see Knock.o).
 */
export function startRock(sphere: SphereConfig, nowMs: number): AsteroidDef | null {
  const g = generateGlobal(sphere);
  const named = namedRocks(sphere);
  if (!named.length) return null;
  const t = nowMs / 1000;
  const scored = named.map((def) => {
    const st = def.drift!.stream!;
    const route = g.streams[st.id]!;
    const { p, s } = streamAt(route, st.u, t);
    const light = lightAt(g.suns, p, def.radius);
    const ahead = (nextStar(route, s)?.ahead ?? Infinity) / STREAM.speed;
    return { def, light, ahead };
  });
  const lit = scored.filter((x) => x.light >= STREAM.lit && x.ahead <= STREAM.starAhead).sort((a, b) => a.ahead - b.ahead);
  return (lit[0] ?? scored.sort((a, b) => b.light - a.light)[0]!).def;
}

/** The start rock's middle at `nowMs` (see startRock), or the middle of the sphere without one: where new players are sent. */
export function startPoint(sphere: SphereConfig, nowMs: number): Vec2 {
  const def = startRock(sphere, nowMs);
  if (!def) return v2();
  const st = def.drift!.stream!;
  return streamAt(generateGlobal(sphere).streams[st.id]!, st.u, nowMs / 1000).p;
}

/** Is a square of half-side `half` round `c` (plus half the flight gap) inside cell bounds `b`? */
function inCell(b: Bounds, c: Vec2, half: number): boolean {
  const pad = half + FLIGHT_GAP / 2;
  return c.x - pad >= b.x0 && c.x + pad <= b.x1 && c.y - pad >= b.y0 && c.y + pad <= b.y1;
}

/** Everything generated inside one area: its cells. */
export function generateArea(sphere: SphereConfig, a: AreaCoord): CellContents {
  const out: CellContents = { asteroids: [], derelicts: [], vines: [], currents: [] };
  for (const [cx, cy] of cellsOfArea(sphere, a)) {
    const cell = generateCell(sphere, cx, cy);
    out.asteroids.push(...cell.asteroids);
    out.derelicts.push(...cell.derelicts);
    out.vines.push(...cell.vines);
    out.currents.push(...cell.currents);
  }
  return out;
}

/**
 * The static world as seen from some areas: the global layer (every sun and
 * current, the streams' tracks and every rock riding them) plus the asteroids,
 * derelicts, vines and eddies of just those areas.
 */
export function generateRegion(sphere: SphereConfig, areas: readonly AreaCoord[]): WorldDef {
  const global = generateGlobal(sphere);
  // Copies: a def's grid origin follows its rock (see placeRocks), and every area and client places its own.
  const asteroids: AsteroidDef[] = global.streamRocks.map((d) => ({ ...d, drift: { ...d.drift!, stream: { ...d.drift!.stream! } } }));
  const derelicts: DerelictDef[] = [];
  const vines: VineDef[] = [];
  const currents: CurrentDef[] = [];
  for (const a of areas) {
    const part = generateArea(sphere, a);
    asteroids.push(...part.asteroids);
    derelicts.push(...part.derelicts);
    vines.push(...part.vines);
    currents.push(...part.currents);
  }
  return { seed: sphere.seed, worldRadius: sphere.radius, suns: global.suns, currents, asteroids, derelicts, vines, streams: global.streams, rings: global.rings };
}

/**
 * Deterministically build a whole world from a seed: every area of a sphere
 * of `radius` (one area unless `areaSize` says otherwise). Both server and
 * client can call this; the server never sends geometry. Asteroid tiles are
 * made separately (generateAsteroidGrid), only for the asteroids needed.
 */
export function generateWorld(seed: number, radius: number = WORLD_RADIUS, areaSize?: number): WorldDef {
  const sphere = areaSize ? { id: "world", seed, radius, areaSize } : singleAreaSphere("world", seed, radius);
  return generateRegion(sphere, allAreas(sphere));
}

/**
 * A new rock formed in cell (cx, cy) after the world was made (see
 * NEW_ROCKS): a small rock, rich in ore, of whatever kind its environment
 * makes it (a living rock near a sun). `taken`: every rock already there
 * (generated and earlier new ones); `ok`: whether a rock may appear there
 * (nobody can see it). Null if there's no room. Its id is `id`; it's made
 * from `seed`, so the same roll makes the same rock.
 */
export function formRock(sphere: SphereConfig, cx: number, cy: number, taken: readonly AsteroidDef[], seed: number, id: string, ok: (center: Vec2, half: number) => boolean): AsteroidDef | null {
  const R = sphere.radius;
  const b = cellBounds(sphere, cx, cy);
  if (distanceToBox(b) > R * 0.88) return null;
  const global = generateGlobal(sphere);
  const rng = new Rng(seed);
  const clear = (center: Vec2, half: number) =>
    inCell(b, center, half) &&
    vLen(center) + half * 1.42 < R * 0.88 &&
    streamsDistance(global.streams, center) > STREAM.corridor + half * 1.42 &&
    !global.suns.some((s) => vDist(s.position, center) < s.radius * 2 + half * 1.42 + FLIGHT_GAP) &&
    !taken.some((a) => !a.drift?.stream && vDist(sweptMiddle(a), center) < (a.w / 2 + sweptReach(a) + half) * 1.42 + FLIGHT_GAP) &&
    ok(center, half);
  // A cell out by the shell is mostly outside it: points there are passed over before anything's made.
  for (let tries = 0, made = 0; tries < 400 && made < 24; tries++) {
    const radius = Math.round(rng.range(NEW_ROCKS.radius[0], NEW_ROCKS.radius[1]));
    const center = v2(Math.round(rng.range(b.x0, b.x1)), Math.round(rng.range(b.y0, b.y1)));
    if (vLen(center) + radius * 1.25 > R * 0.85) continue;
    made++;
    const def = makeRock(sphere, id, center, radius, rng.int(1, 0x7fffffff), rng, clear, { pirates: false });
    if (!def) continue;
    // Its reason for being: what's in it. Metal-rich more often than not.
    if (def.kind !== "living" && def.kind !== "ice" && rng.next() < 0.6) def.kind = "metal";
    def.spawned = true;
    return def;
  }
  return null;
}

/** What kind of small rock an asteroid is, from its own seed and its mineral zone (see WORLD_GEN.rockKinds). */
function rockKind(def: AsteroidDef, zone: "hot" | "mild" | "cold"): AsteroidDef["kind"] {
  let roll = new Rng(def.seed ^ 0x6b1d).next();
  for (const [kind, share] of Object.entries(WORLD_GEN.rockKinds[zone])) {
    if (roll < share) return kind as AsteroidDef["kind"];
    roll -= share;
  }
  return "rock";
}

const centerOf = (a: AsteroidDef) => v2(a.ox + a.w / 2, a.oy + a.h / 2);

/** The middle of the disc a rock may be anywhere in (where it is, or for a drifting one, its path), and how far past its own size that disc reaches. */
const sweptMiddle = (a: AsteroidDef) => (a.drift ? (a.drift.eddy ?? v2(a.drift.x + a.w / 2, a.drift.y + a.h / 2)) : centerOf(a));
const sweptReach = (a: AsteroidDef) => (a.drift ? (a.drift.eddy?.r ?? 0) + ROCK_MOTION.maxKnock : 0);

/**
 * Could rock `a` ever be within `pad` (plus its own size) of `p`? A ring's
 * rocks sweep only their ring, so the lanes between are clear; any other
 * rock, the whole disc it may be anywhere in.
 */
function sweeps(a: AsteroidDef, p: Vec2, pad: number): boolean {
  const e = a.drift?.eddy;
  const size = (a.w / 2) * 1.42 + pad;
  if (e?.inner === undefined) return vDist(sweptMiddle(a), p) < size + sweptReach(a);
  const d = vDist(e, p);
  return d > e.inner - size - ROCK_MOTION.maxKnock && d < e.r + size + ROCK_MOTION.maxKnock;
}

/** Kinds of small rock life can take hold on (and that can grow into living rocks). */
const LIVING: readonly AsteroidDef["kind"][] = ["rock", "hollow", "metal"];

/**
 * The current at `point` (blocks/s), all of them added up: strongest in each
 * one's middle; an eddy's turning with its rocks, and dying away over its
 * outer fifth.
 */
export function currentAt(world: WorldDef, point: Vec2): Vec2 {
  let x = 0;
  let y = 0;
  for (const c of world.currents ?? []) {
    const d = vDist(point, c.position);
    if (d >= c.radius) continue;
    if (c.spin) {
      const k = Math.min(1, (1 - d / c.radius) / 0.2);
      x -= c.spin * (point.y - c.position.y) * k;
      y += c.spin * (point.x - c.position.x) * k;
      continue;
    }
    const k = 1 - (d / c.radius) ** 2;
    x += c.flow.x * k;
    y += c.flow.y * k;
  }
  return v2(x, y);
}

/** Uniform random point in a disc. */
function pointInDisc(rng: Rng, radius: number): Vec2 {
  const a = rng.range(0, Math.PI * 2);
  const r = Math.sqrt(rng.next()) * radius;
  return v2(Math.cos(a) * r, Math.sin(a) * r);
}
